import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { samenvatting } from '../components/ProjectLog';
import { api, type ProjectLogRij } from '../lib/api';
import { wieLabel } from '../lib/format';
import type { ActivityLogEntry } from '@recotex/shared';

function fmtTimeOnly(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' });
}

const ACTION_LABELS: Record<string, { label: string; tone: string }> = {
  'project.created': { label: 'Calculatie aangemaakt', tone: 'chip-success' },
  'project.updated': { label: 'Calculatie gewijzigd', tone: 'chip-neutral' },
  'project.approved': { label: 'Calculatie goedgekeurd', tone: 'chip-accent' },
  'project.deleted': { label: 'Calculatie verwijderd', tone: 'chip-danger' },
  'ploeg.created': { label: 'Ploeg toegevoegd', tone: 'chip-success' },
  'ploeg.updated': { label: 'Ploeg gewijzigd', tone: 'chip-neutral' },
  'ploeg.deleted': { label: 'Ploeg verwijderd', tone: 'chip-danger' },
  'pricelist.created': { label: 'Prijslijst aangemaakt', tone: 'chip-success' },
  'pricelist.updated': { label: 'Prijslijst gewijzigd', tone: 'chip-neutral' },
  'pricelist.deleted': { label: 'Prijslijst verwijderd', tone: 'chip-danger' },
  'pricelist.item.created': { label: 'Item toegevoegd', tone: 'chip-success' },
  'pricelist.item.updated': { label: 'Item gewijzigd', tone: 'chip-neutral' },
  'pricelist.item.deleted': { label: 'Item verwijderd', tone: 'chip-danger' },
  'pricelist.learned': { label: 'Geleerde regel gewijzigd', tone: 'chip-neutral' },
  'user.login': { label: 'Ingelogd', tone: 'chip-neutral' },
  'user.logout': { label: 'Uitgelogd', tone: 'chip-neutral' },
  'user.created': { label: 'Gebruiker aangemaakt', tone: 'chip-success' },
  'user.updated': { label: 'Gebruiker gewijzigd', tone: 'chip-neutral' },
  'user.deleted': { label: 'Gebruiker verwijderd', tone: 'chip-danger' },
  'project.emailed': { label: 'Verstuurd per e-mail', tone: 'chip-accent' },
  'project.whatsapp_sent': { label: 'Verstuurd via WhatsApp', tone: 'chip-accent' },
  'project.extra_regels': { label: 'Extra werken en kosten gewijzigd', tone: 'chip-neutral' },
  'project.toerekening': { label: 'Toegerekend vanuit andere calculatie', tone: 'chip-warning' },
  'project.gevel_variant': { label: 'Gevelprijslijst A/B', tone: 'chip-neutral' },
  'project.overlap': { label: 'Overlap beslist', tone: 'chip-neutral' },
  'project.prijs': { label: 'Prijs gewijzigd', tone: 'chip-warning' },
  'project.prijsafwijking': { label: 'Prijsafwijking', tone: 'chip-warning' },
  'project.oplevering': { label: 'Open voor oplevering', tone: 'chip-neutral' },
  'project.vrijgave': { label: 'Posten vrijgegeven', tone: 'chip-success' },
  'project.nieuwe_versie': { label: 'Nieuwe versie', tone: 'chip-accent' },
  'project.conflict': { label: 'Conflict', tone: 'chip-danger' },
  'project.bevestigd': { label: 'Bevestigd', tone: 'chip-neutral' },
  'project.melding': { label: 'Melding verstuurd', tone: 'chip-accent' },
  'project.oa_gefactureerd': { label: 'Gefactureerd door OA', tone: 'chip-neutral' },
  'project.bouwflow_upload': { label: 'Geplaatst in Bouwflow', tone: 'chip-neutral' },
  'project.tag': { label: 'Label', tone: 'chip-neutral' },
  'project.notitie': { label: 'Notitie', tone: 'chip-neutral' },
  'tag.beheer': { label: 'Labellijst', tone: 'chip-neutral' },
  'rol.beheer': { label: 'Rol gewijzigd', tone: 'chip-neutral' },
};

export function ActivityLog() {
  const [filterEntityType, setFilterEntityType] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterUserId, setFilterUserId] = useState('');

  const q = useQuery({
    queryKey: ['activity', filterEntityType, filterAction, filterUserId],
    queryFn: () => api.activity.list({
      entityType: filterEntityType || undefined,
      action: filterAction || undefined,
      userId: filterUserId || undefined,
      limit: 200,
    }),
  });
  const usersQ = useQuery({ queryKey: ['users-for-activity'], queryFn: () => api.users.list() });

  const entries = q.data?.entries ?? [];
  const users = usersQ.data?.users ?? [];

  const groupedByDay = useMemo(() => {
    const grp = new Map<string, ActivityLogEntry[]>();
    for (const e of entries) {
      const d = new Date(e.createdAt);
      const key = d.toISOString().slice(0, 10);
      if (!grp.has(key)) grp.set(key, []);
      grp.get(key)!.push(e);
    }
    return [...grp.entries()];
  }, [entries]);

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="page-title">Activiteitenlog</h1>
          <p className="text-secondary text-sm mt-1">{q.data?.total ?? 0} activiteiten · wie heeft wat wanneer gedaan</p>
        </div>
      </div>

      <div className="card-flat mb-5 flex flex-wrap gap-4 items-end">
        <div>
          <label className="field-label">Type</label>
          <select className="select min-w-[160px]" value={filterEntityType} onChange={(e) => setFilterEntityType(e.target.value)}>
            <option value="">Alle types</option>
            <option value="project">Projecten</option>
            <option value="ploeg">Onderaannemers</option>
            <option value="pricelist">Prijslijsten</option>
            <option value="pricelist_item">Prijslijst-items</option>
            <option value="user">Gebruikers</option>
          </select>
        </div>
        <div>
          <label className="field-label">Actie</label>
          <select className="select min-w-[180px]" value={filterAction} onChange={(e) => setFilterAction(e.target.value)}>
            <option value="">Alle acties</option>
            <optgroup label="Projecten">
              <option value="project.created">Aangemaakt</option>
              <option value="project.updated">Gewijzigd</option>
              <option value="project.prijs">Prijs gewijzigd</option>
              <option value="project.prijsafwijking">Prijsafwijking</option>
              <option value="project.approved">Goedgekeurd</option>
              <option value="project.notitie">Notitie</option>
              <option value="project.tag">Label</option>
              <option value="project.deleted">Verwijderd</option>
            </optgroup>
            <optgroup label="Onderaannemers">
              <option value="ploeg.created">Aangemaakt</option>
              <option value="ploeg.updated">Gewijzigd</option>
              <option value="ploeg.deleted">Verwijderd</option>
            </optgroup>
            <optgroup label="Prijslijsten">
              <option value="pricelist.item.created">Item toegevoegd</option>
              <option value="pricelist.item.updated">Item gewijzigd</option>
              <option value="pricelist.item.deleted">Item verwijderd</option>
            </optgroup>
          </select>
        </div>
        <div>
          <label className="field-label">Gebruiker</label>
          <select className="select min-w-[200px]" value={filterUserId} onChange={(e) => setFilterUserId(e.target.value)}>
            <option value="">Alle gebruikers</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.displayName} ({u.email})</option>)}
          </select>
        </div>
        {(filterEntityType || filterAction || filterUserId) && (
          <button className="btn-ghost btn-sm" onClick={() => { setFilterEntityType(''); setFilterAction(''); setFilterUserId(''); }}>
            ✕ Filters wissen
          </button>
        )}
      </div>

      {q.isLoading ? (
        <p className="text-secondary">Laden…</p>
      ) : entries.length === 0 ? (
        <div className="card text-center py-12 text-secondary">Geen activiteiten gevonden.</div>
      ) : (
        <div className="space-y-5">
          {groupedByDay.map(([day, dayEntries]) => (
            <div key={day} className="card">
              <h3 className="text-sm font-semibold text-secondary mb-3">{formatDayHeading(day)}</h3>
              <div className="space-y-1">
                {dayEntries.map((e) => <ActivityRow key={e.id} entry={e} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}

function ActivityRow({ entry }: { entry: ActivityLogEntry }) {
  const { titel, regels } = wat(entry);
  const meta = ACTION_LABELS[entry.action] ?? { label: titel, tone: 'chip-neutral' };
  const link = linkVoor(entry);
  const label = entry.entityLabel || entry.entityId;
  return (
    <div className="grid grid-cols-[3.5rem_1fr] gap-3 py-2.5 border-b border-lightgrey last:border-b-0 text-sm normal-case tracking-normal">
      <span className="text-xs text-secondary tabular-nums pt-0.5">{fmtTimeOnly(entry.createdAt)}</span>
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <div className="flex flex-wrap items-baseline gap-2 min-w-0">
            <span className={`${meta.tone} shrink-0`}>{meta.label}</span>
            {link ? <Link to={link} className="font-semibold hover:text-accent-700 hover:underline">{label}</Link> : <strong>{label}</strong>}
            {titel !== meta.label && regels.length === 0 && <span className="text-secondary">{titel}</span>}
          </div>
          <span className="text-xs text-secondary truncate max-w-[220px]">
            {wieLabel(entry.userDisplay || entry.userEmail || '—', entry.persoon)}
          </span>
        </div>
        {regels.length > 0 && (
          <ul className="list-none m-0 mt-1 p-0 text-[13px] text-primary/90 space-y-0.5">
            {titel !== meta.label && <li className="font-semibold">{titel}</li>}
            {regels.slice(0, 6).map((r, i) => <li key={i} className="break-words">{r}</li>)}
            {regels.length > 6 && <li className="text-xs text-secondary">en nog {regels.length - 6} wijzigingen</li>}
          </ul>
        )}
        {entry.reden && <p className="text-xs text-secondary mt-0.5"><span className="font-semibold">Reden:</span> {entry.reden}</p>}
      </div>
    </div>
  );
}

/** Wat er veranderde: dezelfde leesbare samenvatting als de projectlog ("werkpost CREPI: prijs 45 → 48"). */
function wat(e: ActivityLogEntry): { titel: string; regels: string[] } {
  const d = e.details ?? {};
  if (e.action.startsWith('project.') || e.action === 'tag.beheer') {
    return samenvatting({ ...e, details: d, persoon: e.persoon ?? '', reden: e.reden ?? '', categorie: (e.categorie ?? 'calculatie') as ProjectLogRij['categorie'] });
  }
  // Overige acties (onderaannemers, prijslijsten, gebruikers, rollen): de eenvoudige velden tonen.
  const regels: string[] = [];
  if (Array.isArray(d.changed) && d.changed.length) regels.push(`gewijzigd: ${d.changed.join(', ')}`);
  for (const [k, v] of Object.entries(d)) {
    if (k === 'changed' || /id$/i.test(k) || v == null || v === '') continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') regels.push(`${k}: ${typeof v === 'boolean' ? (v ? 'ja' : 'nee') : v}`);
  }
  return { titel: ACTION_LABELS[e.action]?.label ?? e.action, regels };
}

function linkVoor(e: ActivityLogEntry): string | null {
  if (e.action === 'project.deleted') return null;
  if (e.entityType === 'project' && e.entityId) return `/project/${e.entityId}`;
  if (e.projectSleutel) return `/projectnummer/${encodeURIComponent(e.projectSleutel)}`;
  return null;
}

function formatDayHeading(yyyymmdd: string): string {
  const today = new Date().toISOString().slice(0, 10);
  if (yyyymmdd === today) return 'Vandaag';
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  if (yyyymmdd === yesterday) return 'Gisteren';
  const d = new Date(yyyymmdd + 'T00:00:00');
  return d.toLocaleDateString('nl-BE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}
